import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ExportJob, JobStats, JobsAPI } from '../services/jobs-api';
import { useJobsSSE } from '../hooks/useJobsSSE';

interface JobsContextType {
  jobs: ExportJob[];
  runningJobs: ExportJob[];
  stats: JobStats | null;
  isLoading: boolean;
  error: string | null;
  sseConnected: boolean;
  refreshJobs: () => Promise<void>;
  cancelJob: (jobId: string) => Promise<void>;
  getJob: (jobId: string) => ExportJob | undefined;
  showJobsPanel: boolean;
  setShowJobsPanel: (show: boolean) => void;
}

const JobsContext = createContext<JobsContextType | undefined>(undefined);

export const useJobs = () => {
  const context = useContext(JobsContext);
  if (!context) {
    throw new Error('useJobs must be used within JobsProvider');
  }
  return context;
};

interface JobsProviderProps {
  children: React.ReactNode;
}

export const JobsProvider: React.FC<JobsProviderProps> = ({ children }) => {
  const [showJobsPanel, setShowJobsPanel] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Try to use SSE for real-time updates
  const sse = useJobsSSE();

  // Derive jobs from SSE if connected, otherwise use polling fallback
  const jobs = useMemo(() => sse.getAllJobs(), [sse.getAllJobs]);
  const runningJobs = useMemo(() => sse.getRunningJobs(), [sse.getRunningJobs]);
  const stats = useMemo(() => sse.getStats(), [sse.getStats]);
  
  // Loading state: true if SSE is not connected and we have no data yet
  const isLoading = !sse.connected && jobs.length === 0;

  // Polling fallback - only poll if SSE is not connected
  const [pollingJobs, setPollingJobs] = useState<ExportJob[]>([]);
  const [pollingRunningJobs, setPollingRunningJobs] = useState<ExportJob[]>([]);
  const [pollingStats, setPollingStats] = useState<JobStats | null>(null);

  const refreshJobs = useCallback(async () => {
    try {
      setError(null);

      const [allJobs, running, jobStats] = await Promise.all([
        JobsAPI.getAllJobs().catch(() => []),
        JobsAPI.getRunningJobs().catch(() => []),
        JobsAPI.getStats().catch(() => ({ running: 0, completed: 0, failed: 0, cancelled: 0, total: 0 })),
      ]);

      if (sse.connected) {
        // If SSE is connected, manually update SSE state with fetched jobs
        // This ensures newly created jobs appear immediately
        console.log('SSE connected, manually syncing fetched jobs');
        sse.syncJobs(allJobs);
      } else {
        // If SSE not connected, update polling state
        setPollingJobs(allJobs);
        setPollingRunningJobs(running);
        setPollingStats(jobStats);
      }
    } catch (err) {
      console.error('Failed to refresh jobs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load jobs');
    }
  }, [sse.connected, sse.syncJobs]); // Only depend on the specific values/functions we need

  // Use SSE data if connected, otherwise use polling data
  const finalJobs = sse.connected ? jobs : pollingJobs;
  const finalRunningJobs = sse.connected ? runningJobs : pollingRunningJobs;
  const finalStats = sse.connected ? stats : pollingStats;

  // Initial load with polling
  useEffect(() => {
    if (!sse.connected) {
      refreshJobs();
    }
  }, [sse.connected, refreshJobs]);

  // Poll every 2 seconds if SSE is not connected
  useEffect(() => {
    if (sse.connected) {
      // SSE is connected, no need for polling
      return;
    }

    // Set up polling interval (only log on first setup, not on every interval)
    const interval = setInterval(refreshJobs, 2000);
    return () => clearInterval(interval);
  }, [sse.connected, refreshJobs]);

  const cancelJob = useCallback(async (jobId: string) => {
    try {
      await JobsAPI.cancelJob(jobId);
      // SSE will automatically update, or polling will catch it
      if (!sse.connected) {
        await refreshJobs();
      }
    } catch (err) {
      console.error('Failed to cancel job:', err);
      throw err;
    }
  }, [sse.connected, refreshJobs]); // refreshJobs is now stable

  const getJob = useCallback((jobId: string): ExportJob | undefined => {
    if (sse.connected) {
      return sse.getJob(jobId);
    }
    return pollingJobs.find(job => job.id === jobId);
  }, [sse.connected, sse.getJob, pollingJobs]);

  const value: JobsContextType = {
    jobs: finalJobs,
    runningJobs: finalRunningJobs,
    stats: finalStats,
    isLoading,
    error,
    sseConnected: sse.connected,
    refreshJobs,
    cancelJob,
    getJob,
    showJobsPanel,
    setShowJobsPanel,
  };

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>;
};
